import api, { ApiResponse, QueryPayload, QueryResponse, WhereCondition } from './api';
import type { ActivityEntry } from '../components/ActivityCell';

export interface TrafficSourceParam {
  ts_name: string;
  ts_token: string;
  tracked?: boolean;
  in_url?: boolean;  // include in tracking URL (default true)
}

export interface TrafficSourceCustomParam extends TrafficSourceParam {
  index: number;
  name: string;
}

export interface TrafficSourceConfig {
  params: {
    external_ids?: TrafficSourceParam[];
    cost: TrafficSourceParam & { multiply_by?: number };
    custom: TrafficSourceCustomParam[];
    /** ts_name of the param carrying the ad id. */
    cost_grouping_param?: string;
  };
  currency: { code: string };
}

export interface TrafficSource {
  activity_status?: ActivityEntry | null;
  id: string;
  seq?: number;
  ref?: string;
  workspace_id: string;
  tag_ids?: string[];
  name: string;
  kind?: string | null;
  notes?: string | null;
  config: TrafficSourceConfig;
  domains: string[];
  postback_id: string | null;
  partner_id: string | null;
  revshare_pct: number | null;
  status: string;
  created_at: string;
  updated_at: string;
  postback?: { id: string; name: string } | null;
  partner?: { id: string; name: string } | null;
}

export const trafficSourcesService = {
  query: async (filters: { limit?: number; offset?: number; search?: string; where?: WhereCondition ; order_by?: string; order?: 'asc' | 'desc' } = {}): Promise<{ data: TrafficSource[]; total: number }> => {
    const where: WhereCondition | undefined = filters.where ?? (filters.search
      ? { traffic_source: { name: { op: 'ilike', value: `%${filters.search}%` } } } : undefined);
    const payload: QueryPayload = {
      offset: filters.offset ?? 0,
      limit: filters.limit ?? 100,
      order_by: filters.order_by ?? 'traffic_source.created_at',
      order: filters.order ?? 'desc',
      ...(where && { where }),
    };
    const response = await api.post<QueryResponse<TrafficSource>>('/traffic-sources/query', payload);
    return { data: response.data.data, total: response.data._meta?.total ?? 0 };
  },

  getById: async (id: string): Promise<TrafficSource> => {
    const response = await api.get<ApiResponse<TrafficSource>>(`/traffic-sources/${id}`);
    return response.data.data!;
  },

  getKinds: async (): Promise<string[]> => {
    const response = await api.get<ApiResponse<string[]>>('/traffic-sources/kinds');
    return response.data.data ?? [];
  },

  create: async (data: Partial<TrafficSource>): Promise<TrafficSource> => {
    const response = await api.post<ApiResponse<TrafficSource>>('/traffic-sources', data);
    return response.data.data!;
  },

  update: async (id: string, data: Partial<TrafficSource>): Promise<TrafficSource> => {
    const response = await api.patch<ApiResponse<TrafficSource>>(`/traffic-sources/${id}`, data);
    return response.data.data!;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/traffic-sources/${id}`);
  },

  unarchive: async (id: string): Promise<TrafficSource> => {
    const response = await api.patch<ApiResponse<TrafficSource>>(`/traffic-sources/${id}/unarchive`);
    return response.data.data!;
  },
};
